// ─── conditionReportV3.js ─────────────────────────────────────────────────────
// v3 Condition Report fill engine. Loads the hand-maintained master (word_v3),
// locates each section by its heading run text, and replaces the content between
// headings with freshly generated OOXML from the block kit + view model. The master's
// shell, section order and heading styling are preserved. Photos embed via wordOoxml.
import JSZip from 'jszip';
import { buildReportViewModel, filterRegisterToPriority } from './reportViewModel';
import { buildReportSummaries } from './summaryLibrary';
import { buildRecommendationNarrative } from './recommendationLibrary';
import { embedPhotoQueue, photoTokenRun, esc } from './wordOoxml';
import {
  V3, para, run, spacer, headerBand, benefitCallout, priorityRecommendation,
  assetSummary, dashboardTable, conditionCard, coverMeta, coverPhoto, coverLetter, pageBreak,
  sectionHeading, siteItemsTable, siteItemsRowTable, flooringTable, optionComparisonTable,
  referralTable,
} from './wordBlocksV3';

const TEMPLATE_PATH = '/api/template?name=word_v3';
const norm = (s) => (s || '').replace(/\s+/g, ' ').trim().toLowerCase();

// Evidence photo box — uniform for 1, 2 or 3 photos so every card is the same height
// and page breaking stays predictable. Three across (5,874,000 EMU) plus two separator
// runs fits the 6,149,340 EMU usable cell width. See the plan's geometry section.
export const PHOTO_BOX = { w: 1958000, h: 2700000 };
export const MAX_PHOTOS_PER_ROW = 3;

// Hero cover photo. Aspect-fit inside this box (never cropped, never distorted).
// Max height before the cover spills to page 2 is 7,637,780 EMU — see plan geometry.
export const HERO_BOX = { w: 5093970, h: 6791960 };
export const HERO_DOCPR_ID = 800; // register photos start at 900; must stay distinct

// Every heading the engine anchors on. The guard test asserts each exists in the
// master — a rename that lands in only one place would silently ship mock content.
export const V3_ANCHORS = ['Higgins Recommendation', 'Area Summary', 'Detailed Condition Register'];
const [ANCHOR_RECOMMENDATION, ANCHOR_AREA_SUMMARY, ANCHOR_REGISTER] = V3_ANCHORS;

// One shared drawing-id counter across every section appended after the register.
// wp:docPr/@id must be unique document-wide (Word refuses to open a file with
// duplicates) — HERO_DOCPR_ID = 800 is the cover photo; the register and every
// section after it share this counter starting at 900, so no section may declare
// its own local counter.
function makeDocprSeq(start = 900) {
  let n = start;
  return { next: () => n++ };
}

// Every <w:p>…</w:p> match in document order, with its visible text normalized and
// its offsets. Shared by findHeadingPara (needs offsets) and headingTextsOf (doesn't).
// Attribute-position regexes use [^>/]* so a self-closing '<w:p .../>' can't be
// mis-parsed as an opening tag (which would run on and swallow the next real paragraph).
function paragraphs(xml) {
  const re = /<w:p\b[^>/]*>[\s\S]*?<\/w:p>/g;
  const out = [];
  let m;
  while ((m = re.exec(xml))) {
    const text = norm((m[0].match(/<w:t[^>/]*>([\s\S]*?)<\/w:t>/g) || [])
      .map((t) => t.replace(/<[^>]+>/g, '')).join(''));
    out.push({ text, start: m.index, end: m.index + m[0].length });
  }
  return out;
}

// Every paragraph's visible text, normalized exactly as findHeadingPara matches it.
// Exported so the anchor-drift guard test cannot drift from the matcher it guards.
export function headingTextsOf(documentXml) {
  return paragraphs(documentXml).map((p) => p.text);
}

// Index of the <w:p …>…</w:p> whose visible text (normalized) equals `heading`.
// Returns {start,end} or null. start = index of '<w:p', end = index just after '</w:p>'.
function findHeadingPara(xml, heading) {
  const target = norm(heading);
  const found = paragraphs(xml).find((p) => p.text === target);
  return found ? { start: found.start, end: found.end } : null;
}

// Replace everything between the `startHeading` paragraph and the `endHeading`
// paragraph (both kept) with `newXml`. If endHeading is null, replace to just before
// the final <w:sectPr>. Missing anchors → return xml unchanged (never corrupt).
function replaceRegion(xml, startHeading, endHeading, newXml) {
  const s = findHeadingPara(xml, startHeading);
  if (!s) return xml;
  let endIdx;
  if (endHeading) {
    const e = findHeadingPara(xml.slice(s.end), endHeading);
    if (!e) return xml;
    endIdx = s.end + e.start;
  } else {
    const sect = xml.indexOf('<w:sectPr', s.end);
    if (sect === -1) return xml;
    // back up to the start of the <w:p> that wraps the final sectPr, if any
    const pWrap = xml.lastIndexOf('<w:p ', sect);
    endIdx = pWrap > s.end ? pWrap : sect;
  }
  return xml.slice(0, s.end) + newXml + xml.slice(endIdx);
}

// Replace the span [first <w:tbl>, start of `endHeading`) with newXml. The cover has no
// heading of its own, so it is bounded by the header-band table and the first real
// heading. Missing anchors → xml unchanged (never corrupt).
function replaceCoverRegion(xml, endHeading, newXml) {
  const tblIdx = xml.indexOf('<w:tbl>');
  if (tblIdx === -1) return xml;
  const end = findHeadingPara(xml, endHeading);
  if (!end || end.start <= tblIdx) return xml;
  return xml.slice(0, tblIdx) + newXml + xml.slice(end.start);
}

// Cover page + letter. The hero rides the existing photoQueue; embedPhotoQueue
// aspect-fits it to HERO_BOX exactly as it does evidence photos to PHOTO_BOX.
export function buildCoverXml(project, vm, summaries, photoQueue) {
  const meta = vm.meta || {};
  let hero = '';
  const coverUrl = (project && project.reportMeta && project.reportMeta.coverPhotoUrl) || '';
  if (coverUrl) {
    const token = '@@PHOTO_' + HERO_DOCPR_ID + '@@';
    photoQueue.push({ token, url: coverUrl, docprId: HERO_DOCPR_ID, maxW: HERO_BOX.w, maxH: HERO_BOX.h });
    hero = photoTokenRun(token);
  }
  const salutation = meta.clientContact ? `Dear ${meta.clientContact},` : 'Dear Sir/Madam,';
  const site = meta.siteName || 'the site';
  const addr = (project && project.address) || '';
  const opener = addr
    ? `We recently completed a thorough condition assessment at ${site}, ${addr}.`
    : `We recently completed a thorough condition assessment at ${site}.`;

  return headerBand(meta)
    + coverMeta(meta)
    + coverPhoto(hero)
    + pageBreak()
    + coverLetter({ salutation, opener, summary: summaries.overall || '' })
    + benefitCallout(vm.benefitCount);
}

// Each card header carries the full "<Area> - <Surface>" label. The area heading
// above the group can be pages back by the time a reader reaches a card (or lands
// on a different page from it entirely), so the card has to say for itself which
// area it describes — "Upstairs Foyer - Walls", not a bare "Walls".
export const qualifiedSurface = (area, substrate) =>
  (area && substrate) ? `${area} - ${substrate}` : (substrate || area || '');

// Lead-in for a priority-only register. A reader who counts twelve areas in the
// Area Summary and then finds four detail pages needs to be told the detail was
// scoped on purpose, not that eight areas went missing.
export const PRIORITY_REGISTER_LEAD =
  'This register details the areas where painting work is needed — those rated Amber '
  + '(Consider Painting) or Red (Repaint ASAP). Areas rated Green are serviceable and '
  + 'are listed in the Area Summary above.';
export const PRIORITY_REGISTER_EMPTY =
  'No areas were rated Amber or Red — no painting work is required at this time. '
  + 'Every assessed area is listed in the Area Summary above.';

// Build the register body (area groups → summary → cards → photo tokens).
// docprId increments per PHOTO (not per row) via the shared `seq` counter — Word
// requires document-wide uniqueness across every section, not just this one.
// `options.priorityOnly` narrows the body to Amber/Red rows (see
// filterRegisterToPriority); every other section of the report is untouched, so
// the Area Summary still accounts for the whole site.
function buildRegisterXml(vm, summaries, photoQueue, seq, options = {}) {
  const priorityOnly = options.priorityOnly === true;
  const groups = priorityOnly ? filterRegisterToPriority(vm.register) : (vm.register || []);
  let out = '';
  if (priorityOnly) {
    out += para(run(groups.length ? PRIORITY_REGISTER_LEAD : PRIORITY_REGISTER_EMPTY,
      { sz: 18, italic: true, color: V3.ink }), { after: 200 });
  }
  groups.forEach((group) => {
    out += para(run(group.area, { sz: 22, bold: true, color: V3.slate }), { before: 160, after: 40, keepNext: true });
    const s = (summaries.areas || {})[group.area];
    if (s) out += para(run(s, { sz: 18, italic: true, color: V3.ink }), { after: 80, keepNext: true });
    group.rows.forEach((r) => {
      const photos = (r.photos || []).slice(0, MAX_PHOTOS_PER_ROW).map((p) => {
        const docprId = seq.next();
        const token = '@@PHOTO_' + docprId + '@@';
        photoQueue.push({ token, url: p.url, docprId, maxW: PHOTO_BOX.w, maxH: PHOTO_BOX.h });
        return photoTokenRun(token);
      });
      out += conditionCard({ substrate: qualifiedSurface(group.area, r.substrate), rag: r.rag, note: r.note, action: r.action, outcome: r.outcome, reason: r.reason, photos });
      out += spacer(240);
    });
  });
  return out;
}

// Scope Options (P4) — Option 1 (full spec) vs Option 2 (recommended). Omitted
// entirely when the two options never differ, so a site with no reduced scope
// stays out of the comparison table altogether.
function buildOptionsXml(vm) {
  const rows = vm.optionComparison || [];
  if (!rows.length) return '';
  return pageBreak()
    + sectionHeading('Scope Options')
    + para(run('Option 1 applies full specification throughout. Option 2 is our recommended '
             + 'scope, reducing treatment where the existing coating still performs.',
             { sz: 18, color: V3.ink }), { after: 160 })
    + optionComparisonTable(rows);
}

// Internal Flooring (§4). Omitted entirely when nothing is recorded. Flooring has
// no photo strip in the export — the table carries condition + recommendation
// only — so this section takes no photoQueue/seq, unlike its neighbours; the
// wiring below still passes them for a consistent call shape across sections.
function buildFlooringXml(vm) {
  const rows = vm.flooring || [];
  if (!rows.length) return '';
  return pageBreak() + sectionHeading('Internal Flooring') + flooringTable(rows);
}

// "Other Specified Items" (§5). Omitted entirely when nothing is recorded — the
// report should stay short for a simple site. Uses the same shared `seq` counter
// as the register for any blind photos.
function buildSiteItemsXml(vm, photoQueue, seq) {
  const si = vm.siteItems;
  if (!si || (!si.counts.length && !si.tables.length && !si.representativeRequests)) return '';

  let out = pageBreak() + sectionHeading('Other Specified Items');

  if (si.counts.length) {
    const showMarkup = si.counts.some((r) => r.markup && r.markup !== '—');
    out += siteItemsTable(si.counts, { showMarkup }) + spacer(200);

    const blindsRow = si.counts.find((r) => Array.isArray(r.photos) && r.photos.length);
    if (blindsRow) {
      const photoRuns = blindsRow.photos.slice(0, MAX_PHOTOS_PER_ROW).map((p) => {
        const docprId = seq.next();
        const token = '@@PHOTO_' + docprId + '@@';
        photoQueue.push({ token, url: p.url, docprId, maxW: PHOTO_BOX.w, maxH: PHOTO_BOX.h });
        return photoTokenRun(token);
      });
      if (photoRuns.length) {
        out += para(photoRuns.join(run('  ', { sz: 18 })), { align: 'center', after: 200 });
      }
    }
  }

  si.tables.forEach((t) => { out += siteItemsRowTable(t.label, t.cols, t.rows) + spacer(200); });

  if (si.representativeRequests) {
    out += sectionHeading('Site Representative Requests')
      + para(run(si.representativeRequests, { sz: 18, color: V3.ink }), { after: 120 });
  }

  return out;
}

// Building Repairs for Client Action (P5). Appended last — a closing note, not
// part of the assessment proper. Omitted entirely when nothing is flagged.
function buildReferralXml(vm) {
  const rows = vm.referrals || [];
  if (!rows.length) return '';
  return pageBreak()
    + sectionHeading('Building Repairs for Client Action')
    + para(run('The items below are building repairs outside the painting scope. We recommend '
             + 'they are reviewed and, where applicable, addressed prior to painting works.',
             { sz: 18, color: V3.ink }), { after: 160 })
    + referralTable(rows);
}

// PURE: produce the filled document.xml + the photo queue (no fetch).
// `options.priorityOnly` produces the partial report — same cover, recommendation
// and Area Summary, with the Detailed Condition Register cut to the Amber/Red
// areas where painting work is needed.
export function fillV3DocumentXml(documentXml, project, options = {}) {
  const areas = (project && project.areas) || [];
  const vm = buildReportViewModel(project, areas);
  const summaries = buildReportSummaries(project, vm);
  const reco = buildRecommendationNarrative({ ...project, areas }, vm);
  const photoQueue = [];
  let xml = documentXml;

  // Cover page + letter + Good-news callout: replaces everything from the header band
  // to the first heading. The master's `Executive Summary` heading lies inside this
  // span and is removed by the replace — no template edit needed.
  xml = replaceCoverRegion(xml, ANCHOR_RECOMMENDATION, buildCoverXml(project, vm, summaries, photoQueue));

  // Higgins Recommendation region (through per-building) → tiers + whole-site asset summary
  xml = replaceRegion(xml, ANCHOR_RECOMMENDATION, ANCHOR_AREA_SUMMARY,
    priorityRecommendation(reco) + spacer(160) + assetSummary(vm.stats));

  // Area Summary region → dashboard table, then a hard break so the register
  // starts on a fresh page. The break must precede the register HEADING, and
  // replaceRegion preserves start headings — so it belongs at the end of THIS region.
  xml = replaceRegion(xml, ANCHOR_AREA_SUMMARY, ANCHOR_REGISTER,
    dashboardTable(vm.dashboardRows) + pageBreak());

  // Detailed Condition Register region → area groups + cards + photo tokens, then
  // Other Specified Items (Site Items, §5). Both sections share one docPr-id
  // counter — a second section that started its own count at 900 would collide
  // with the register's ids and Word would refuse to open the file.
  const seq = makeDocprSeq(900);
  xml = replaceRegion(xml, ANCHOR_REGISTER, null,
    buildRegisterXml(vm, summaries, photoQueue, seq, { priorityOnly: options.priorityOnly })
    + buildOptionsXml(vm)
    + buildFlooringXml(vm, photoQueue, seq)
    + buildSiteItemsXml(vm, photoQueue, seq)
    + buildReferralXml(vm));

  return { documentXml: xml, photoQueue };
}

// PURE: fill the running-header placeholders (FORM ID / SITE NAME) and strip yellow
// highlight. jobRef/siteName are raw user data — escaped here, once, on their way into
// the header XML (mirrors WordExport.js's esc(jobRef)/esc(siteName) at the same point).
// Exported so the string transformation can be unit-tested without a fetch/zip fixture.
export function fillHeaderXml(headerXml, jobRef, siteName) {
  const ejobRef = esc(jobRef);
  const esiteName = esc(siteName);
  let hx = headerXml
    .split('FORM ID – SITE NAME').join(ejobRef)
    .split('FORM ID - SITE NAME').join(ejobRef)
    .split('SITE NAME').join(esiteName);
  hx = hx.replace(/<w:highlight w:val="yellow"\/>/g, '');
  return hx;
}

// Fill the running header parts across every word/header*.xml part in the zip.
function fillHeaderParts(zip, jobRef, siteName) {
  const names = Object.keys(zip.files).filter((n) => /^word\/header\d+\.xml$/.test(n));
  return Promise.all(names.map(async (hn) => {
    const before = await zip.file(hn).async('text');
    const hx = fillHeaderXml(before, jobRef, siteName);
    if (hx !== before) zip.file(hn, hx);
  }));
}

// BROWSER entry: fetch template, fill, embed photos, download. Returns filename.
export async function exportConditionReportV3(project, options = {}) {
  const resp = await fetch(TEMPLATE_PATH, { headers: options.idToken ? { Authorization: `Bearer ${options.idToken}` } : {} });
  if (!resp.ok) throw new Error('Could not load the Condition Report template — please sign in and try again.');
  const zip = await JSZip.loadAsync(await resp.arrayBuffer());
  let xml = await zip.file('word/document.xml').async('text');

  const filled = fillV3DocumentXml(xml, project, { priorityOnly: options.priorityOnly });
  xml = filled.documentXml;

  const meta = project.reportMeta || {};
  const siteName = meta.siteName || project.projectName || project.clientName || 'Site';
  const inspDate = meta.inspectionDate || project.date || new Date().toISOString().slice(0, 10);
  const jobRef = project.jobNumber ? (project.jobNumber + ' – ' + siteName) : siteName;
  await fillHeaderParts(zip, jobRef, siteName);

  if (filled.photoQueue.length) xml = await embedPhotoQueue(zip, xml, filled.photoQueue);
  xml = xml.replace(/<w:highlight w:val="yellow"\/>/g, '');
  zip.file('word/document.xml', xml);

  const out = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 6 } });
  const safe = (siteName || 'Site').replace(/[^a-zA-Z0-9\s]/g, '').trim().replace(/\s+/g, '_');
  const kind = options.priorityOnly ? 'Condition_Report_Priority_Areas' : 'Condition_Report';
  const fname = [safe, kind, inspDate].filter(Boolean).join('_') + '.docx';
  const url = URL.createObjectURL(out);
  const a = document.createElement('a');
  a.href = url; a.download = fname;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
  return fname;
}
